import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box, Card, Grid, Stack, Typography, Chip, Button, TextField, Autocomplete,
  InputAdornment, FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Switch, ToggleButtonGroup, ToggleButton,
  useTheme, useMediaQuery
} from "@mui/material";
import { DataGrid, type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import FunctionsIcon from "@mui/icons-material/Functions";
import AddBoxIcon from "@mui/icons-material/AddBox";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DeleteIcon from "@mui/icons-material/Delete";
import { PageHeader } from "../components/PageHeader.js";
import { EmptyState } from "../components/EmptyState.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { RowQuickActions } from "../components/RowQuickActions.js";
import { ComboServices } from "../components/combos/index.js";
import { useApi } from "../hooks/useApi.js";
import { ui } from "../store/uiStore.js";
import { formLabel } from "../lib/labels.js";
import { currentPeriod, type Credit, type Service, type PartnerReduced } from "@kappa-v2/contracts";

const eur = (n?: number) => `€ ${(n ?? 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`;

const CURRENT_YEAR = new Date().getFullYear();
const PERIODS: string[] = [];
for (let y = 2024; y <= CURRENT_YEAR + 2; y++) {
  for (let m = 1; m <= 12; m++) PERIODS.push(`${String(m).padStart(2, "0")}/${y}`);
}

interface GenerationResult {
  created: number;
  skipped: number;
  affectedPersons: number;
}

interface Template {
  serviceId: string;
  name: string;
  description: string;
  price: number;
  emailNotification: boolean;
  isDeducible: boolean;
  serviceType: string;
}

const EMPTY_TEMPLATE: Template = {
  serviceId: "", name: "", description: "", price: 0,
  emailNotification: false, isDeducible: false, serviceType: ""
};

export function CreditsGenerationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { call } = useApi();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [services, setServices] = useState<Service[]>([]);
  // `allPartners` = tutti (per risolvere i nomi anche dei soci inattivi con crediti);
  // la griglia di assegnazione usa solo gli attivi (derivati sotto).
  const [allPartners, setAllPartners] = useState<PartnerReduced[]>([]);
  const [credits, setCredits] = useState<Credit[]>([]);
  const [loadingCredits, setLoadingCredits] = useState(false);

  const [period, setPeriod] = useState(currentPeriod());
  const [template, setTemplate] = useState<Template>(EMPTY_TEMPLATE);
  // Filtro per tipo socio: "" = tutti, oppure owner/horse/pony.
  const [typeFilter, setTypeFilter] = useState("");
  // Filtro per stato: "true" = attivi (default) | "false" = inattivi | "all" = tutti.
  const [statusFilter, setStatusFilter] = useState<"all" | "true" | "false">("true");
  // Socio selezionato dalla combo: se valorizzato, i pannelli mostrano solo lui.
  const [selectedPartnerId, setSelectedPartnerId] = useState("");
  const [onlySelected, setOnlySelected] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [confirmCalc, setConfirmCalc] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Credit | null>(null);

  const unwrap = <T,>(v: { data?: T[] } | T[]): T[] => (Array.isArray(v) ? v : (v.data ?? []));

  // Caricamenti statici (servizi + soci attivi)
  useEffect(() => {
    call<{ data?: Service[] } | Service[]>("/api/v1/services", { silent: true })
      .then((r) => setServices(unwrap(r))).catch(() => setServices([]));
    call<{ data?: PartnerReduced[] } | PartnerReduced[]>("/api/v1/partners/reduced?active=all")
      .then((r) => setAllPartners(unwrap(r))).catch(() => setAllPartners([]));
  }, [call]);

  const fetchCredits = useCallback(() => {
    setLoadingCredits(true);
    call<{ data?: Credit[] } | Credit[]>(`/api/v1/payments/credits?period=${encodeURIComponent(period)}`)
      .then((r) => setCredits(unwrap(r)))
      .catch(() => setCredits([]))
      .finally(() => setLoadingCredits(false));
  }, [call, period]);

  useEffect(() => { fetchCredits(); }, [fetchCredits]);

  const serviceById = useMemo(() => {
    const m = new Map<string, Service>();
    services.forEach((s) => s._id && m.set(String(s._id), s));
    return m;
  }, [services]);

  // Mappa su TUTTI i soci → risolve i nomi anche per gli inattivi che hanno crediti.
  const partnerById = useMemo(() => {
    const m = new Map<string, PartnerReduced>();
    allPartners.forEach((p) => p._id && m.set(String(p._id), p));
    return m;
  }, [allPartners]);

  // Solo soci attivi per la griglia di assegnazione.
  const activePartners = useMemo(
    () => allPartners.filter((p) => p.active !== false),
    [allPartners]
  );

  // Selezione servizio → precompila il modello (l'admin può modificare tutto).
  const onSelectService = (id: string) => {
    const s = serviceById.get(id);
    if (!s) { setTemplate(EMPTY_TEMPLATE); return; }
    setTemplate({
      serviceId: id,
      name: s.name ?? "",
      description: s.description ?? "",
      price: s.price ?? 0,
      emailNotification: Boolean(s.emailNotification),
      isDeducible: Boolean(s.isDeducible),
      serviceType: s.serviceType ?? ""
    });
  };

  const templateReady =
    Boolean(template.serviceId) &&
    template.name.trim().length > 0 &&
    template.description.trim().length > 0 &&
    // Prezzo valido se diverso da zero: i negativi sono ammessi (sconti/storni).
    template.price !== 0;

  const handleCalculate = async () => {
    setConfirmCalc(false);
    setCalculating(true);
    try {
      const res = await call<GenerationResult>("/api/v1/payments/credits/period", {
        method: "POST",
        body: { period }
      });
      ui.toast(
        t("creditsGen.calcResult", { created: res.created, skipped: res.skipped, affected: res.affectedPersons }),
        "success"
      );
      fetchCredits();
    } finally {
      setCalculating(false);
    }
  };

  const handleAddToPartner = async (p: PartnerReduced) => {
    const body = {
      personId: String(p._id),
      serviceId: template.serviceId,
      period,
      credit: Number(template.price),
      name: template.name.trim(),
      description: template.description.trim(),
      emailNotification: template.emailNotification,
      isDeducible: template.isDeducible,
      fromPartnerService: false,
      partnerType: p.partnerType ?? "form.partnerType.other",
      serviceType: template.serviceType
    };
    await call("/api/v1/payments/credits", { method: "POST", body });
    ui.toast(t("creditsGen.addedToast", { name: p.fullName }), "success");
    fetchCredits();
  };

  const handleDelete = async () => {
    if (!deleteTarget?._id) return;
    await call(`/api/v1/payments/credits/${deleteTarget._id}`, { method: "DELETE" });
    setDeleteTarget(null);
    fetchCredits();
  };

  const partnerName = (id: string) => partnerById.get(id)?.fullName ?? id;

  const buildCreditActions = (c: Credit) => [
    {
      key: "receipt", label: t("creditsGen.openReceipt"), description: t("creditsGen.openReceipt"),
      icon: <ReceiptLongIcon fontSize="small" />,
      onClick: () => navigate(`/protected/receipts?personId=${c.personId}`)
    },
    {
      key: "delete", label: t("creditsGen.deleteCredit"), description: t("creditsGen.deleteCredit"),
      icon: <DeleteIcon fontSize="small" />, destructive: true, dividerBefore: true,
      onClick: () => setDeleteTarget(c)
    }
  ];

  // Soci filtrati per TIPO + STATO (alimentano la combo e la lista quando nessun socio è selezionato).
  const partnersByFilter = useMemo(() => {
    return allPartners
      .filter((p) => statusFilter === "all" ? true : statusFilter === "true" ? p.active !== false : p.active === false)
      .filter((p) => !typeFilter || p.partnerType === typeFilter)
      .sort((a, b) => (a.fullName ?? "").localeCompare(b.fullName ?? "", "it"));
  }, [allPartners, statusFilter, typeFilter]);

  // Lista del pannello: se un socio è selezionato dalla combo → solo lui; altrimenti tutti i filtrati.
  const filteredPartners = useMemo(() => {
    if (selectedPartnerId) {
      const p = partnerById.get(selectedPartnerId);
      return p ? [p] : [];
    }
    return partnersByFilter;
  }, [partnersByFilter, selectedPartnerId, partnerById]);

  const filteredCredits = useMemo(() => {
    let list = onlySelected && template.serviceId
      ? credits.filter((c) => c.serviceId === template.serviceId)
      : credits;
    // Socio selezionato → solo i suoi crediti del periodo.
    if (selectedPartnerId) list = list.filter((c) => c.personId === selectedPartnerId);
    // Ordine per ultimo inserito (più recente in cima): createdAt desc, fallback su _id.
    const ts = (c: Credit) => (c.createdAt ? new Date(c.createdAt).getTime() : 0);
    return [...list].sort((a, b) => {
      const d = ts(b) - ts(a);
      return d !== 0 ? d : String(b._id ?? "").localeCompare(String(a._id ?? ""));
    });
  }, [credits, onlySelected, template.serviceId, selectedPartnerId]);

  const partnerColumns: GridColDef[] = [
    {
      field: "partnerType", headerName: t("creditsGen.typeSocio"), width: 130,
      renderCell: (p: GridRenderCellParams<PartnerReduced>) => (
        <Chip size="small" variant="outlined" label={formLabel(p.row.partnerType)} sx={{ fontSize: "0.72rem" }} />
      )
    },
    {
      field: "fullName", headerName: t("creditsGen.socio"), flex: 1, minWidth: 180,
      renderCell: (p: GridRenderCellParams<PartnerReduced>) => (
        <Typography variant="body2" fontWeight={600}>{p.row.fullName}</Typography>
      )
    },
    {
      field: "actions", headerName: "", width: 56, sortable: false, align: "center", headerAlign: "center",
      renderCell: (p: GridRenderCellParams<PartnerReduced>) => (
        <Button
          data-tour="cg-add"
          size="small"
          disabled={!templateReady}
          onClick={() => handleAddToPartner(p.row)}
          title={t("creditsGen.addToPartner")}
          sx={{ minWidth: 0, px: 1 }}
        >
          <AddBoxIcon fontSize="small" />
        </Button>
      )
    }
  ];

  const creditColumns: GridColDef[] = [
    {
      field: "personId", headerName: t("creditsGen.socio"), flex: 1, minWidth: 160,
      renderCell: (p: GridRenderCellParams<Credit>) => (
        <Typography variant="body2" fontWeight={600}>{partnerName(p.row.personId)}</Typography>
      )
    },
    {
      field: "serviceType", headerName: t("creditsGen.serviceType"), width: 120,
      renderCell: (p: GridRenderCellParams<Credit>) => (
        <Chip size="small" variant="outlined" label={formLabel(p.row.serviceType)} sx={{ fontSize: "0.72rem" }} />
      )
    },
    { field: "description", headerName: t("common.description"), flex: 1.3, minWidth: 200 },
    {
      field: "fromPartnerService", headerName: t("creditsGen.fromSubscription"), width: 130, align: "center", headerAlign: "center",
      renderCell: (p: GridRenderCellParams<Credit>) => (
        <Chip size="small" variant="outlined" label={p.row.fromPartnerService ? t("common.yes") : t("common.no")}
          color={p.row.fromPartnerService ? "primary" : "default"} sx={{ fontSize: "0.72rem" }} />
      )
    },
    {
      field: "credit", headerName: t("common.amount"), width: 110, align: "right", headerAlign: "right",
      renderCell: (p: GridRenderCellParams<Credit>) => (
        <Typography variant="body2" fontWeight={700}>{eur(p.row.credit)}</Typography>
      )
    },
    {
      field: "actions", headerName: "", width: 90, sortable: false, align: "right", headerAlign: "center",
      renderCell: (p: GridRenderCellParams<Credit>) => (
        <RowQuickActions ariaLabel={t("common.actions")} quickKeys={["delete"]} items={buildCreditActions(p.row)} />
      )
    }
  ];

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <PageHeader
        title={t("creditsGen.title")}
        subtitle={t("creditsGen.subtitle")}
        onRefresh={fetchCredits}
        actions={
          <Stack direction="row" spacing={1.5} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 130 }} data-tour="cg-period">
              <InputLabel>{t("common.period")}</InputLabel>
              <Select label={t("common.period")} value={period} onChange={(e) => setPeriod(e.target.value)} MenuProps={{ PaperProps: { sx: { maxHeight: 4 * 48 } } }}>
                {PERIODS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </Select>
            </FormControl>
            <Button
              data-tour="cg-calc"
              variant="contained"
              startIcon={<FunctionsIcon />}
              onClick={() => setConfirmCalc(true)}
              disabled={calculating}
            >
              {t("creditsGen.calculate")}
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, flex: 1 }}>
        {/* Modello servizio */}
        <Card sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>{t("creditsGen.serviceTemplate")}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
            {t("creditsGen.templateHint")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8} data-tour="cg-service">
              <ComboServices label={t("creditsGen.selectService")} value={template.serviceId} onChange={onSelectService} />
            </Grid>
            <Grid item xs={6} md={2} data-tour="cg-notify">
              <FormControlLabel
                control={<Switch checked={template.emailNotification}
                  onChange={(e) => setTemplate((tpl) => ({ ...tpl, emailNotification: e.target.checked }))} />}
                label={t("creditsGen.notifications")}
              />
            </Grid>
            <Grid item xs={6} md={2} data-tour="cg-deducible">
              <FormControlLabel
                control={<Switch checked={template.isDeducible}
                  onChange={(e) => setTemplate((tpl) => ({ ...tpl, isDeducible: e.target.checked }))} />}
                label={t("creditsGen.deductible")}
              />
            </Grid>
            <Grid item xs={12} md={3} data-tour="cg-price">
              <TextField fullWidth size="small" label={t("creditsGen.priceLabel")} type="number"
                value={template.price}
                onChange={(e) => setTemplate((tpl) => ({ ...tpl, price: Number(e.target.value) }))}
                inputProps={{ step: 0.01 }}
                error={Boolean(template.serviceId) && template.price === 0}
                helperText={Boolean(template.serviceId) && template.price === 0 ? t("creditsGen.priceError") : undefined}
              />
            </Grid>
            <Grid item xs={12} data-tour="cg-desc">
              <TextField fullWidth size="small" label={t("creditsGen.descriptionLabel")} multiline minRows={2}
                value={template.description}
                onChange={(e) => setTemplate((tpl) => ({ ...tpl, description: e.target.value }))}
                error={Boolean(template.serviceId) && template.description.trim().length === 0}
                helperText={Boolean(template.serviceId) && template.description.trim().length === 0 ? t("creditsGen.descriptionError") : undefined}
              />
            </Grid>
          </Grid>
        </Card>

        {/* Filtri condivisi (valgono per Soci e per i Crediti): combo a sx, toggle a dx */}
        <Card sx={{ p: 1.5, mb: 2 }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <Autocomplete
              size="small"
              sx={{ flex: 1, minWidth: 240 }}
              options={partnersByFilter}
              value={selectedPartnerId ? (partnerById.get(selectedPartnerId) ?? null) : null}
              getOptionLabel={(o) => o.fullName ?? ""}
              isOptionEqualToValue={(o, v) => String(o._id) === String(v._id)}
              onChange={(_, v) => setSelectedPartnerId(v ? String(v._id) : "")}
              renderInput={(params) => (
                <TextField {...params} placeholder={t("creditsGen.searchPartner")}
                  InputProps={{ ...params.InputProps, startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: "text.disabled" }} /></InputAdornment> }}
                />
              )}
            />
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
              <ToggleButtonGroup size="small" color="primary" exclusive
                value={typeFilter} onChange={(_, v) => v !== null && setTypeFilter(v)}>
                <ToggleButton value="">{t("partners.allTypes")}</ToggleButton>
                <ToggleButton value="form.partnerType.owner">{t("form.partnerType.owner")}</ToggleButton>
                <ToggleButton value="form.partnerType.horse">{t("form.partnerType.horse")}</ToggleButton>
                <ToggleButton value="form.partnerType.pony">{t("form.partnerType.pony")}</ToggleButton>
              </ToggleButtonGroup>
              <ToggleButtonGroup size="small" color="primary" exclusive
                value={statusFilter} onChange={(_, v) => v !== null && setStatusFilter(v)}>
                <ToggleButton value="all">{t("common.all")}</ToggleButton>
                <ToggleButton value="true">{t("receipts.activePartners")}</ToggleButton>
                <ToggleButton value="false">{t("receipts.inactivePartners")}</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Box>
        </Card>

        <Grid container spacing={2}>
          {/* Soci */}
          <Grid item xs={12} md={5} data-tour="cg-partners">
            <Card>
              <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,.06)", minHeight: 56, display: "flex", alignItems: "center" }}>
                <Typography variant="subtitle1" fontWeight={700}>{t("creditsGen.activePartners")}</Typography>
              </Box>
              {activePartners.length === 0
                ? <EmptyState title={t("creditsGen.noPartners")} />
                : <DataGrid rows={filteredPartners} columns={partnerColumns} getRowId={(r) => r._id ?? Math.random().toString()}
                    autoHeight rowHeight={60} pageSizeOptions={[10, 25, 50]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    disableRowSelectionOnClick sx={{
                      border: "none",
                      "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
                      "& .MuiDataGrid-cell--textRight": { justifyContent: "flex-end" },
                      "& .MuiDataGrid-cell--textLeft": { justifyContent: "flex-start" },
                      "& .MuiDataGrid-cell--textCenter": { justifyContent: "center" }
                    }}
                  />
              }
            </Card>
          </Grid>

          {/* Crediti del periodo */}
          <Grid item xs={12} md={7} data-tour="cg-credits">
            <Card>
              <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,.06)", minHeight: 56, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                  {t("creditsGen.periodCredits", { period })}
                </Typography>
                <FormControlLabel data-tour="cg-only"
                  control={<Switch size="small" checked={onlySelected} onChange={(e) => setOnlySelected(e.target.checked)} />}
                  label={t("creditsGen.onlySelected")}
                />
              </Box>
              {!loadingCredits && filteredCredits.length === 0
                ? <EmptyState title={t("creditsGen.noCredits")} />
                : isMobile
                  ? (
                    <Box>
                      {filteredCredits.map((c, idx) => (
                        <Box key={c._id ?? idx} sx={{ p: 2, borderTop: idx ? "1px solid rgba(0,0,0,.06)" : "none" }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={700}>{partnerName(c.personId)}</Typography>
                              <Typography variant="caption" color="text.secondary" display="block">{c.description}</Typography>
                            </Box>
                            <RowQuickActions ariaLabel={t("common.actions")} quickKeys={["delete"]} items={buildCreditActions(c)} />
                          </Box>
                          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", alignItems: "center" }}>
                            <Typography variant="body2" fontWeight={700}>{eur(c.credit)}</Typography>
                            <Chip size="small" variant="outlined" label={formLabel(c.serviceType)} sx={{ fontSize: "0.72rem" }} />
                            <Chip size="small" variant="outlined"
                              label={`${t("creditsGen.fromSubscription")}: ${c.fromPartnerService ? t("common.yes") : t("common.no")}`}
                              color={c.fromPartnerService ? "primary" : "default"} sx={{ fontSize: "0.72rem" }} />
                          </Stack>
                        </Box>
                      ))}
                    </Box>
                  )
                  : <DataGrid rows={filteredCredits} columns={creditColumns} getRowId={(r) => r._id ?? Math.random().toString()}
                      loading={loadingCredits} autoHeight rowHeight={60} pageSizeOptions={[10, 25, 50, 100]}
                      initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                      disableRowSelectionOnClick sx={{
                        border: "none",
                        "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
                        "& .MuiDataGrid-cell--textRight": { justifyContent: "flex-end" },
                        "& .MuiDataGrid-cell--textLeft": { justifyContent: "flex-start" },
                        "& .MuiDataGrid-cell--textCenter": { justifyContent: "center" }
                      }}
                    />
              }
            </Card>
          </Grid>
        </Grid>
      </Box>

      <ConfirmDialog
        open={confirmCalc}
        title={t("creditsGen.calculate")}
        message={t("creditsGen.calculateConfirm", { period })}
        confirmLabel={t("creditsGen.calculate")}
        onConfirm={handleCalculate}
        onCancel={() => setConfirmCalc(false)}
      />
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        message={t("creditsGen.deleteConfirm", {
          name: deleteTarget?.name ?? "",
          partner: deleteTarget ? partnerName(deleteTarget.personId) : ""
        })}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
